import { useCallback } from 'react';
import { format } from 'date-fns';
import { localPoint } from '@visx/event';
import { bisector } from 'd3-array';

import type { scaleLinear, scaleUtc } from '@visx/scale';

import { useTheme } from '@simplywallst/ui-core';

import { VisxText } from '@/components/VisxText';

interface Props {
  data: [Date, number][];
  interactionPoints: Record<
    string,
    {
      market?: number | undefined;
      portfolio?: number | undefined;
    }
  >;
  index: number;
  xScale: ReturnType<typeof scaleUtc<number>>;
  yScale: ReturnType<typeof scaleLinear<number>>;
  setIndex: (index: number) => void;
}

const bisectDate = bisector((d) => d).left;

export const InteractionLayer = ({
  data,
  interactionPoints,
  xScale,
  yScale,
  index,
  setIndex,
}: Props) => {
  const theme = useTheme();

  const [chartHeight, chartTop] = yScale.range();
  const [chartLeft, chartWidth] = xScale.range();

  const height = chartTop + chartHeight;
  const width = chartLeft + chartWidth;

  const [date] = data[index] ? data[index] : [];

  const metric = date && interactionPoints[date.toISOString().slice(0, 10)];

  const x = xScale(new Date(date || 0));

  const handleMove = useCallback(
    (e) => {
      const point = localPoint(e);

      // Get the mouse x coordinate relative to the chart
      let x = 0;
      if (point !== null && point.x >= 0) {
        x = point.x;
      } else if (point !== null && point.x < 0) {
        // Fallback if Firefox has a negative point.x
        const rect = e.target.getBoundingClientRect();
        x = e.clientX - rect.x;
      }

      const date = xScale.invert(x);
      const nextIndex = bisectDate(
        data.map((n) => n[0]),
        date
      );

      if (nextIndex === index) return;
      if (nextIndex > data.length - 1) return;

      setIndex(nextIndex);
    },
    [index, setIndex, data, xScale]
  );

  if (width < 0) return null;

  return (
    <svg width={width} height={height}>
      <VisxText
        x={x}
        y={2}
        textAnchor="middle"
        verticalAnchor="start"
        typography="tiny"
        fill={theme.color.white}
      >
        {date ? format(date, 'MMM dd') : `n/a`}
      </VisxText>
      <rect
        y={chartTop}
        x={x}
        width="1"
        height={chartHeight}
        fill={theme.color.brand01}
      />
      <circle
        transform={`translate(${x} ${yScale(metric?.portfolio || 0)})`}
        r="3"
        fill={theme.color.white}
        stroke="white"
        strokeWidth="1"
      />
      {metric?.market && (
        <circle
          transform={`translate(${x} ${yScale(metric?.market || 0)})`}
          r="3"
          fill={theme.color.white}
          stroke="white"
          strokeWidth="1"
        />
      )}
      <rect
        width={width}
        height={height}
        fill="transparent"
        onMouseMove={handleMove}
        onTouchMove={handleMove}
      />
    </svg>
  );
};
